import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Plus, Minus, Trash2, Check, History, X } from 'lucide-react';
import api from '../utils/api';
import { generarClientFormId } from '../utils/idempotency';

const num = (n) => Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });

export default function SolicitarInsumos() {
  const navigate = useNavigate();
  const [insumos, setInsumos]   = useState([]);
  const [q, setQ]               = useState('');
  const [loading, setLoading]   = useState(false);
  const [carro, setCarro]       = useState([]);        // [{ insumo, cantidad }]
  const [ascensores, setAscensores] = useState([]);
  const [ascensorId, setAscensorId] = useState('');
  const [trabajos, setTrabajos]     = useState([]);
  const [trabajoId, setTrabajoId]   = useState('');
  const [nota, setNota]         = useState('');
  const [enviando, setEnviando] = useState(false);
  const [okMsg, setOkMsg]       = useState('');
  const [errMsg, setErrMsg]     = useState('');
  const [verHistorial, setVerHistorial] = useState(false);
  const [historial, setHistorial] = useState([]);

  // UUID de idempotencia: uno por "carro". Se conserva en los reintentos y se
  // renueva solo tras un envío exitoso.
  const requestIdRef = useRef(generarClientFormId());

  const cargarInsumos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/solicitudes-insumos/insumos', { params: { q: q || undefined } });
      setInsumos(r.data);
    } catch { setErrMsg('No se pudo cargar el catálogo'); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(cargarInsumos, 250); // debounce búsqueda
    return () => clearTimeout(t);
  }, [cargarInsumos]);

  useEffect(() => {
    api.get('/ascensores').then(r => setAscensores(r.data)).catch(() => {});
    api.get('/solicitudes-insumos/trabajos').then(r => setTrabajos(r.data || [])).catch(() => {});
  }, []);

  const enCarro = (id) => carro.find(c => c.insumo.id === id);

  const agregar = (insumo) => {
    setCarro(prev => {
      const ex = prev.find(c => c.insumo.id === insumo.id);
      if (ex) return prev.map(c => c.insumo.id === insumo.id ? { ...c, cantidad: c.cantidad + 1 } : c);
      return [...prev, { insumo, cantidad: 1 }];
    });
  };
  const cambiarCant = (id, delta) => {
    setCarro(prev => prev
      .map(c => c.insumo.id === id ? { ...c, cantidad: Math.max(0, +(c.cantidad + delta).toFixed(2)) } : c)
      .filter(c => c.cantidad > 0));
  };
  const setCantExacta = (id, val) => {
    const n = Number(val);
    setCarro(prev => prev.map(c => c.insumo.id === id ? { ...c, cantidad: isNaN(n) ? 0 : n } : c));
  };
  const quitar = (id) => setCarro(prev => prev.filter(c => c.insumo.id !== id));

  const registrar = async () => {
    setErrMsg(''); setOkMsg('');
    const lineas = carro.filter(c => c.cantidad > 0).map(c => ({ insumo_id: c.insumo.id, cantidad: c.cantidad }));
    if (lineas.length === 0) { setErrMsg('Agrega al menos un insumo'); return; }
    setEnviando(true);
    try {
      await api.post('/solicitudes-insumos', {
        lineas,
        nota: nota || undefined,
        ascensor_id: ascensorId ? Number(ascensorId) : undefined,
        trabajo_id: trabajoId ? Number(trabajoId) : undefined,
        client_request_id: requestIdRef.current,
      });
      setOkMsg('Retiro registrado. Stock descontado y enviado a revisión.');
      setCarro([]); setNota(''); setAscensorId(''); setTrabajoId('');
      requestIdRef.current = generarClientFormId(); // nuevo id para el próximo retiro
      cargarInsumos();
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'No se pudo registrar el retiro. Intenta de nuevo.');
    } finally { setEnviando(false); }
  };

  const abrirHistorial = async () => {
    setVerHistorial(true);
    try { const r = await api.get('/solicitudes-insumos/mias'); setHistorial(r.data); }
    catch { setHistorial([]); }
  };

  const totalItems = carro.reduce((a, c) => a + c.cantidad, 0);

  return (
    <div className="app-container">
      {/* Header */}
      <div style={{
        background: '#0B0F0D', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A9994', padding: 4, display: 'flex' }}>
          <ArrowLeft size={22} />
        </button>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 11, color: '#6B7C74' }}>Bodega</p>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#F4F7F5' }}>Solicitar insumos</h1>
        </div>
        <button onClick={abrirHistorial} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A9994', padding: 6, display: 'flex' }}>
          <History size={20} />
        </button>
      </div>

      <div className="page-content" style={{ paddingBottom: carro.length ? 200 : 24 }}>
        {okMsg && <div style={{ background: '#12382D', color: '#4CAF50', padding: '12px 14px', borderRadius: 10, margin: '4px 0 12px', fontSize: 13 }}>{okMsg}</div>}
        {errMsg && <div style={{ background: '#2D0F0F', color: '#EF4444', padding: '12px 14px', borderRadius: 10, margin: '4px 0 12px', fontSize: 13 }}>{errMsg}</div>}

        {/* Buscador */}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar insumo…"
          style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #2E4A40', borderRadius: 10,
                   background: '#0D1311', color: '#F4F7F5', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />

        {loading ? <p style={{ color: '#8A9994' }}>Cargando…</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {insumos.length === 0 && <p style={{ color: '#6B7C74', textAlign: 'center', padding: '20px 0' }}>Sin insumos disponibles.</p>}
            {insumos.map(it => {
              const c = enCarro(it.id);
              return (
                <div key={it.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12,
                  background: '#0D1311', border: `1px solid ${c ? '#0F6E56' : 'rgba(255,255,255,0.06)'}`,
                }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#12382D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Package size={18} color="#4CAF50" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#F4F7F5' }}>{it.nombre}</div>
                    <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 2 }}>
                      {it.categoria} · disponible: {num(it.stock_actual)} {it.unidad_medida}
                    </div>
                  </div>
                  {c ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => cambiarCant(it.id, -1)} style={btnRedondo}><Minus size={15} /></button>
                      <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 700, color: '#F4F7F5' }}>{num(c.cantidad)}</span>
                      <button onClick={() => cambiarCant(it.id, +1)} style={btnRedondo}><Plus size={15} /></button>
                    </div>
                  ) : (
                    <button onClick={() => agregar(it)} style={{
                      background: '#0F6E56', border: 'none', borderRadius: 9, padding: '8px 12px',
                      color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}><Plus size={14} /> Agregar</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Barra inferior fija con el carro */}
      {carro.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, background: '#111814',
          borderTop: '1px solid rgba(255,255,255,0.08)', padding: '14px 16px',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.4)', zIndex: 90,
        }}>
          <div style={{ maxHeight: 132, overflowY: 'auto', marginBottom: 10 }}>
            {carro.map(c => (
              <div key={c.insumo.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ flex: 1, fontSize: 13, color: '#C9D3CE', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.insumo.nombre}</span>
                <input type="number" value={c.cantidad} onChange={e => setCantExacta(c.insumo.id, e.target.value)}
                  style={{ width: 64, padding: '5px 8px', border: '1.5px solid #2E4A40', borderRadius: 7, background: '#0D1311', color: '#F4F7F5', fontSize: 13, textAlign: 'right' }} />
                <span style={{ fontSize: 11, color: '#6B7C74', width: 44 }}>{c.insumo.unidad_medida}</span>
                <button onClick={() => quitar(c.insumo.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 2, display: 'flex' }}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          <select value={ascensorId} onChange={e => setAscensorId(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #2E4A40', borderRadius: 9, background: '#0D1311', color: '#F4F7F5', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}>
            <option value="">Comunidad / ascensor (opcional)</option>
            {ascensores.map(a => (
              <option key={a.id} value={a.id}>{a.comunidad}{a.n_ascensor ? ` — Asc. ${a.n_ascensor}` : ''}</option>
            ))}
          </select>
          {trabajos.length > 0 && (
            <select value={trabajoId} onChange={e => setTrabajoId(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #2E4A40', borderRadius: 9, background: '#0D1311', color: '#F4F7F5', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}>
              <option value="">Orden de trabajo (opcional)</option>
              {trabajos.map(t => (
                <option key={t.id} value={t.id}>{t.etiqueta}{t.comunidad ? ` — ${t.comunidad}` : ''}</option>
              ))}
            </select>
          )}
          <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota (opcional)"
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #2E4A40', borderRadius: 9, background: '#0D1311', color: '#F4F7F5', fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />

          <button onClick={registrar} disabled={enviando} style={{
            width: '100%', background: enviando ? '#0b4d3c' : '#0F6E56', border: 'none', borderRadius: 11,
            padding: '13px', color: '#fff', fontSize: 15, fontWeight: 700, cursor: enviando ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Check size={18} /> {enviando ? 'Registrando…' : `Registrar retiro (${num(totalItems)} ítems)`}
          </button>
        </div>
      )}

      {/* Modal historial "mis retiros" */}
      {verHistorial && (
        <div onClick={() => setVerHistorial(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#111814', borderRadius: '20px 20px 0 0', width: '100%', padding: '20px 16px', maxHeight: '75vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#F4F7F5' }}>Mis retiros</span>
              <button onClick={() => setVerHistorial(false)} style={{ background: 'none', border: 'none', color: '#8A9994', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {historial.length === 0 ? (
              <p style={{ color: '#6B7C74', textAlign: 'center', padding: '24px 0' }}>Sin retiros aún.</p>
            ) : historial.map(s => (
              <div key={s.id} style={{ padding: '12px 14px', borderRadius: 12, marginBottom: 8, background: '#0D1311', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#8A9994' }}>{String(s.created_at).slice(0, 10)}{s.comunidad ? ` · ${s.comunidad}` : ''}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: s.estado === 'revisada' ? '#4CAF50' : '#E0A415' }}>
                    {s.estado === 'revisada' ? 'Revisado' : 'Por revisar'}
                  </span>
                </div>
                {(s.lineas || []).map((l, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#C9D3CE' }}>· {l.insumo} — {num(l.cantidad)} {l.unidad_medida}</div>
                ))}
                {s.nota && <div style={{ fontSize: 12, color: '#6B7C74', marginTop: 4 }}>{s.nota}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const btnRedondo = {
  width: 30, height: 30, borderRadius: 8, border: '1.5px solid #2E4A40', background: '#161C19',
  color: '#C9D3CE', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
